import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CalendarDays, MapPin, Clock, Sparkles, Loader2, PartyPopper, Utensils, Pencil,
  CalendarPlus, Download, Image as ImageIcon, Lock, Heart, Plus, X, Info, ChevronDown,
} from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import { AspectRatio } from '../components/ui/aspect-ratio';
import { ThemeToggle } from '../components/landing/ThemeToggle';
import { usePublicTheme } from '../hooks/usePublicTheme';
import { getFontTheme, ensureFontLoaded } from '../lib/fontThemes';
import { formatEventTime, formatDateLong } from '../lib/time';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const photoSrc = (url) => (url && url.startsWith('/') ? `${BACKEND_URL}${url}` : url);

export default function PublicEvent() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('i') || '';
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [status, setStatus] = useState('yes');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [editing, setEditing] = useState(false);
  const [hp, setHp] = useState(''); // honeypot

  // edit-later
  const [showLoad, setShowLoad] = useState(false);
  const [loadEmail, setLoadEmail] = useState('');
  const [loadingExisting, setLoadingExisting] = useState(false);

  const [gallery, setGallery] = useState([]);
  const [defaultTheme, setDefaultTheme] = useState(null);
  const { theme: mode, toggle } = usePublicTheme(defaultTheme);

  const refresh = () => api.get(`/public/events/${slug}`, { params: { i: inviteToken } }).then((r) => setEvent(r.data)).catch(() => {});

  useEffect(() => {
    api.get(`/public/events/${slug}`, { params: { i: inviteToken } })
      .then((r) => {
        setEvent(r.data);
        if (r.data?.invite_email) setEmail((e) => e || r.data.invite_email);
        if (r.data?.invite_name) setName((n) => n || r.data.invite_name);
        // Seed a real default party size so the field isn't a phantom "1".
        const hasGuestCount = (r.data?.rsvp_fields || []).some((f) => f.key === 'guest_count' && f.type === 'number');
        if (hasGuestCount) setResponses((prev) => (prev.guest_count == null ? { guest_count: '1', ...prev } : prev));
      })
      .catch(() => setNotFound(true)).finally(() => setLoading(false));
    api.get(`/public/events/${slug}/gallery`).then((r) => setGallery(r.data)).catch(() => {});
    api.get('/public/landing').then((r) => setDefaultTheme(r.data?.default_theme || 'light')).catch(() => setDefaultTheme('light'));
  }, [slug, inviteToken]);

  const setResp = (key, val) => setResponses((r) => ({ ...r, [key]: val }));

  // --- Potluck (structured "what are you bringing") helpers ---
  const [openInfo, setOpenInfo] = useState({});
  const getPot = (key) => {
    const v = responses[key];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return { unsure: !!v.unsure, items: Array.isArray(v.items) ? v.items : [] };
    }
    // Legacy free-text value -> seed as a single item so it isn't lost
    if (typeof v === 'string' && v.trim()) return { unsure: false, items: [{ category: '', item: v }] };
    return { unsure: false, items: [] };
  };
  const addPotItem = (key, cats) => { const p = getPot(key); setResp(key, { ...p, unsure: false, items: [...p.items, { category: (cats && cats[0]) || '', item: '' }] }); };
  const updPotItem = (key, i, patch) => { const p = getPot(key); setResp(key, { ...p, items: p.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }); };
  const rmPotItem = (key, i) => { const p = getPot(key); setResp(key, { ...p, items: p.items.filter((_, idx) => idx !== i) }); };
  const setUnsure = (key, val) => { const p = getPot(key); setResp(key, { ...p, unsure: val, items: val ? [] : p.items }); };

  const loadExisting = async () => {
    if (!loadEmail) { toast.error('Enter your email'); return; }
    setLoadingExisting(true);
    try {
      const r = await api.get(`/public/events/${slug}/myrsvp`, { params: { email: loadEmail, i: inviteToken } });
      const data = r.data;
      setName(data.name || '');
      setEmail(data.email || '');
      setStatus(data.status || 'yes');
      setResponses(data.responses || {});
      setEditing(true);
      setShowLoad(false);
      setDone(false);
      toast.success('We found your RSVP — update it below.');
      document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No RSVP found for that email');
    } finally {
      setLoadingExisting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    const cleaned = { ...responses };
    if (status !== 'no') {
      for (const f of (event.rsvp_fields || [])) {
        if (f.type === 'potluck') {
          const p = getPot(f.key);
          const items = p.unsure ? [] : p.items
            .filter((it) => (it.item || '').trim())
            .map((it) => ({ category: (it.category || '').trim(), item: (it.item || '').trim() }));
          cleaned[f.key] = { unsure: !!p.unsure, items };
          if (f.required && !p.unsure && items.length === 0) {
            toast.error(`Please fill in: ${f.label}`); return;
          }
        } else if (f.required && !responses[f.key]) {
          toast.error(`Please fill in: ${f.label}`); return;
        }
      }
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/public/events/${slug}/rsvp`, { email, name, status, responses: cleaned, token: inviteToken, website: hp });
      setDone(true);
      setEditing(res.data.updated);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit RSVP');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="tg-public min-h-screen flex items-center justify-center bg-background" data-theme={mode}><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (notFound) return <div className="tg-public min-h-screen flex flex-col items-center justify-center bg-background text-foreground text-center px-6" data-theme={mode}><h1 className="font-display text-3xl font-semibold">Event not found</h1><p className="text-muted-foreground mt-2">This invitation link may have expired.</p></div>;

  const theme = event.theme_color || '#2F6B57';
  const accent = event.accent_color || '#C86B4A';
  const ft = getFontTheme(event.font_theme);
  const fontVars = { '--tg-font-display': ft.display, '--tg-font-body': ft.body };
  ensureFontLoaded(event.font_theme);
  const mapsUrl = event.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}` : null;
  const fields = event.rsvp_fields || [];
  const shared = event.shared_responses || {};
  const sharedWithItems = Object.entries(shared).filter(([, v]) => v.items && v.items.length);
  const groupByCategory = (items) => {
    const map = new Map();
    for (const it of (items || [])) {
      const c = it.category || 'Other';
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(it);
    }
    return Array.from(map.entries());
  };

  return (
    <div className="tg-public tg-fontscope min-h-screen bg-background text-foreground pb-16" style={fontVars} data-theme={mode} data-testid="public-event-page">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle theme={mode} onToggle={toggle} />
      </div>
      <div className="relative tg-noise px-6 pt-14 pb-20 text-center" style={{ background: `linear-gradient(135deg, ${theme}, ${accent})` }}>
        {event.cover_image && <img src={event.cover_image} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="relative max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-1.5 text-white/85 text-xs uppercase tracking-[0.2em] mb-4"><Sparkles className="h-3.5 w-3.5" /> You&apos;re Invited</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold text-white leading-tight">{event.title}</h1>
          {event.host && <p className="text-white/85 mt-3">Hosted by {event.host}</p>}
        </div>
      </div>

      {event.use_custom_flyer && event.custom_flyer_url && (
        <div className="max-w-2xl mx-auto px-6 pt-8" data-testid="public-custom-flyer">
          <img src={`${BACKEND_URL}${event.custom_flyer_url}`} alt={event.title}
            className="w-full rounded-2xl border border-border shadow-[0_20px_60px_rgba(17,24,39,0.12)]" />
          {event.custom_flyer_pdf_url && (
            <div className="text-center mt-3">
              <a href={`${BACKEND_URL}${event.custom_flyer_pdf_url}`} target="_blank" rel="noreferrer"
                className="text-sm font-medium inline-flex items-center gap-1" style={{ color: theme }}>
                <Download className="h-3.5 w-3.5" /> Download flyer (PDF)
              </a>
            </div>
          )}
        </div>
      )}

      <div className={`max-w-2xl mx-auto px-6 relative ${event.use_custom_flyer && event.custom_flyer_url ? 'mt-6' : '-mt-12'}`}>
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-[0_20px_60px_rgba(17,24,39,0.12)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: theme + '15' }}><CalendarDays className="h-5 w-5" style={{ color: theme }} /></div>
              <div><div className="text-[11px] uppercase tracking-widest text-muted-foreground">When</div><div className="font-medium mt-0.5">{event.date ? formatDateLong(event.date) : 'TBA'}</div>{(event.time || event.end_time) && <div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5 tabular-nums"><Clock className="h-3.5 w-3.5" /> {formatEventTime(event)}</div>}</div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: accent + '15' }}><MapPin className="h-5 w-5" style={{ color: accent }} /></div>
              <div><div className="text-[11px] uppercase tracking-widest text-muted-foreground">Where</div><div className="font-medium mt-0.5">{event.location_name || 'TBA'}</div>{event.address && <div className="text-sm text-muted-foreground mt-0.5">{event.address}</div>}{mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-sm font-medium mt-1 inline-block" style={{ color: theme }}>Get directions →</a>}</div>
            </div>
          </div>
          {event.description && <div className="mt-6 pt-6 border-t border-border"><p className="text-foreground/80 leading-relaxed whitespace-pre-line">{event.description}</p></div>}
          <div className="mt-6 pt-6 border-t border-border flex flex-wrap gap-2" data-testid="public-calendar-buttons">
            <a href={event.google_calendar_url} target="_blank" rel="noreferrer" className="flex-1 min-w-[140px]">
              <Button variant="secondary" className="w-full gap-2"><CalendarPlus className="h-4 w-4" /> Google Calendar</Button>
            </a>
            <a href={`${BACKEND_URL}${event.ics_url}`} className="flex-1 min-w-[140px]">
              <Button variant="secondary" className="w-full gap-2"><Download className="h-4 w-4" /> Apple / Outlook</Button>
            </a>
          </div>
        </div>

        {/* Coordination list */}
        {sharedWithItems.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 mt-5" data-testid="coordination-list">
            <h3 className="font-display text-xl font-semibold mb-4 flex items-center gap-2"><Utensils className="h-5 w-5" style={{ color: accent }} /> What people are bringing</h3>
            <div className="space-y-5">
              {sharedWithItems.map(([key, v]) => (
                <div key={key}>
                  <div className="text-sm font-medium mb-2">{v.label}</div>
                  {v.grouped ? (
                    <div className="space-y-3">
                      {groupByCategory(v.items).map(([cat, items]) => (
                        <div key={cat}>
                          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5" style={{ color: accent }}>{cat}</div>
                          <ul className="space-y-1.5">
                            {items.map((it, i) => (
                              <li key={i} className="text-sm flex gap-2">
                                <span className="font-medium text-foreground">{it.value}</span>
                                {!v.anonymous && it.name && <span className="text-muted-foreground">— {it.name}</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="space-y-1.5">
                      {v.items.map((it, i) => (
                        <li key={i} className="text-sm flex gap-2">
                          <span className="font-medium text-foreground">{Array.isArray(it.value) ? it.value.join(', ') : it.value}</span>
                          {!v.anonymous && it.name && <span className="text-muted-foreground">— {it.name}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Event gallery */}
        {gallery.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 mt-5" data-testid="event-gallery">
            <h3 className="font-display text-xl font-semibold mb-4 flex items-center gap-2"><ImageIcon className="h-5 w-5" style={{ color: theme }} /> Gallery</h3>
            <Carousel opts={{ loop: gallery.length > 1, align: 'start' }} className="w-full">
              <CarouselContent>
                {gallery.map((p) => (
                  <CarouselItem key={p.id} className="sm:basis-4/5" data-testid="gallery-slide">
                    <div className="relative rounded-xl overflow-hidden border border-border">
                      <AspectRatio ratio={16 / 9}>
                        <img src={photoSrc(p.url)} alt={p.caption || 'Event photo'} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      </AspectRatio>
                      {p.caption && <div className="absolute bottom-2 left-2 px-2.5 py-1 rounded-full bg-black/55 text-white text-xs">{p.caption}</div>}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="h-9 w-9" />
              <CarouselNext className="h-9 w-9" />
            </Carousel>
          </div>
        )}

        {/* RSVP */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 mt-5" id="rsvp">
          {!event.invited ? (
            <div className="text-center py-6" data-testid="rsvp-invite-only">
              <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4"><Lock className="h-7 w-7 text-muted-foreground" /></div>
              <h3 className="font-display text-2xl font-semibold">RSVP by invitation</h3>
              <p className="text-muted-foreground mt-2 max-w-md mx-auto">This event is private. Please use the personal link from your invitation — by email or on your printed invite — to RSVP and view the full address.</p>
            </div>
          ) : done ? (
            <div className="text-center py-6" data-testid="rsvp-success">
              {(() => {
                const isNo = status === 'no';
                const isMaybe = status === 'maybe';
                const Icon = isNo ? Heart : PartyPopper;
                const title = isNo
                  ? (editing ? 'RSVP updated' : 'Thanks for letting us know')
                  : (editing ? 'RSVP updated!' : 'Thank you!');
                const message = isNo
                  ? "Sorry you can't make it this time — we'll miss you! Your response has been recorded."
                  : isMaybe
                    ? "You're marked as a maybe — we hope you can make it! Your response has been recorded."
                    : "Your RSVP is in — we can't wait to see you!";
                return (
                  <>
                    <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4"><Icon className="h-7 w-7 text-primary" /></div>
                    <h3 className="font-display text-2xl font-semibold" data-testid="rsvp-success-title">{title}</h3>
                    <p className="text-muted-foreground mt-2" data-testid="rsvp-success-message">{message}</p>
                  </>
                );
              })()}
              {event.rsvp_allow_edit && (
                <Button variant="secondary" className="mt-5 gap-2" onClick={() => { setDone(false); }} data-testid="edit-again-button"><Pencil className="h-4 w-4" /> Edit my response</Button>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 mb-1">
                <h3 className="font-display text-2xl font-semibold">{editing ? 'Update your RSVP' : 'Will you be there?'}</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-5">Let the host know if you can make it.</p>
              <form onSubmit={submit} className="space-y-4">
                <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={hp} onChange={(e) => setHp(e.target.value)} style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
                <div className="grid grid-cols-3 gap-2">
                  {[{ v: 'yes', l: 'Yes' }, { v: 'maybe', l: 'Maybe' }, { v: 'no', l: 'No' }].map((o) => (
                    <button type="button" key={o.v} onClick={() => setStatus(o.v)}
                      className={`h-11 rounded-xl border text-sm font-medium transition-colors ${status === o.v ? 'border-transparent text-white' : 'border-border hover:bg-secondary'}`}
                      style={status === o.v ? { background: theme } : {}} data-testid={`rsvp-${o.v}`}>{o.l}</button>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="rsvp-name-input" /></div>
                  <div className="space-y-1.5"><Label>Email *</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="rsvp-email-input" /></div>
                </div>

                {/* Dynamic fields (hidden when not attending) */}
                {status !== 'no' && fields.map((f) => (
                  <div key={f.key} className="space-y-1.5" data-testid={`rsvp-field-${f.key}`}>
                    <Label>{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
                    {f.type === 'textarea' && <Textarea value={responses[f.key] || ''} onChange={(e) => setResp(f.key, e.target.value)} rows={3} />}
                    {f.type === 'text' && <Input value={responses[f.key] || ''} onChange={(e) => setResp(f.key, e.target.value)} className="h-11" />}
                    {f.type === 'number' && <Input type="number" min={f.key === 'guest_count' ? '1' : '0'} value={responses[f.key] ?? ''} onChange={(e) => setResp(f.key, e.target.value)} onFocus={(e) => e.target.select()} className="h-11" placeholder={f.key === 'guest_count' ? '1' : '0'} />}
                    {f.type === 'yesno' && (
                      <div className="grid grid-cols-2 gap-2">
                        {['Yes', 'No'].map((opt) => (
                          <button type="button" key={opt} onClick={() => setResp(f.key, opt)}
                            className={`h-11 rounded-xl border text-sm font-medium transition-colors ${responses[f.key] === opt ? 'border-transparent text-white' : 'border-border hover:bg-secondary'}`}
                            style={responses[f.key] === opt ? { background: accent } : {}}>{opt}</button>
                        ))}
                      </div>
                    )}
                    {f.type === 'choice' && (
                      <Select value={responses[f.key] || ''} onValueChange={(v) => setResp(f.key, v)}>
                        <SelectTrigger className="h-11"><SelectValue placeholder="Choose…" /></SelectTrigger>
                        <SelectContent>{(f.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    {f.type === 'potluck' && (() => {
                      const p = getPot(f.key);
                      const cats = (f.categories && f.categories.length ? f.categories : ['Main dish', 'Side item']);
                      const infoOpen = !!openInfo[f.key];
                      return (
                        <div className="space-y-2.5">
                          {f.explainer && (
                            <div className="rounded-xl border border-border bg-secondary/40 overflow-hidden">
                              <button type="button" onClick={() => setOpenInfo((o) => ({ ...o, [f.key]: !o[f.key] }))}
                                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm font-medium text-left hover:bg-secondary/60 transition-colors"
                                data-testid={`potluck-explainer-toggle-${f.key}`}>
                                <span className="flex items-center gap-1.5"><Info className="h-4 w-4" style={{ color: accent }} /> About bringing a dish</span>
                                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${infoOpen ? 'rotate-180' : ''}`} />
                              </button>
                              {infoOpen && <p className="px-3 pb-3 text-sm text-muted-foreground whitespace-pre-line" data-testid={`potluck-explainer-text-${f.key}`}>{f.explainer}</p>}
                            </div>
                          )}
                          {!p.unsure && (
                            <div className="space-y-2">
                              {p.items.map((it, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  <Select value={it.category || ''} onValueChange={(v) => updPotItem(f.key, i, { category: v })}>
                                    <SelectTrigger className="h-11 w-[42%] min-w-[120px]" data-testid={`potluck-category-${f.key}-${i}`}><SelectValue placeholder="Category" /></SelectTrigger>
                                    <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                  </Select>
                                  <Input value={it.item || ''} onChange={(e) => updPotItem(f.key, i, { item: e.target.value })} placeholder="e.g. Potato salad" className="h-11 flex-1" data-testid={`potluck-item-${f.key}-${i}`} />
                                  <Button type="button" size="icon" variant="ghost" onClick={() => rmPotItem(f.key, i)} className="flex-shrink-0 text-muted-foreground" aria-label="Remove dish"><X className="h-4 w-4" /></Button>
                                </div>
                              ))}
                              <Button type="button" variant="secondary" size="sm" className="gap-1.5" onClick={() => addPotItem(f.key, cats)} data-testid={`add-potluck-item-${f.key}`}>
                                <Plus className="h-3.5 w-3.5" /> {p.items.length ? 'Add another dish' : 'Add a dish'}
                              </Button>
                            </div>
                          )}
                          {f.allow_unsure && (
                            <label className="flex items-center gap-2 cursor-pointer text-sm select-none">
                              <input type="checkbox" checked={p.unsure} onChange={(e) => setUnsure(f.key, e.target.checked)} className="h-4 w-4 rounded border-border" style={{ accentColor: accent }} data-testid={`potluck-unsure-${f.key}`} />
                              <span className="text-muted-foreground">I&apos;m not sure yet — I&apos;ll decide later</span>
                            </label>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ))}

                <Button type="submit" disabled={submitting} className="w-full h-11 gap-2" style={{ background: accent }} data-testid="rsvp-submit-button">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}{submitting ? 'Submitting…' : editing ? 'Update RSVP' : 'Send RSVP'}
                </Button>
              </form>

              {event.rsvp_allow_edit && !editing && (
                <div className="mt-4 pt-4 border-t border-border text-center">
                  {!showLoad ? (
                    <button type="button" className="text-sm font-medium" style={{ color: theme }} onClick={() => setShowLoad(true)} data-testid="edit-existing-toggle">Already responded? Edit your RSVP</button>
                  ) : (
                    <div className="flex gap-2">
                      <Input value={loadEmail} onChange={(e) => setLoadEmail(e.target.value)} placeholder="Enter the email you used" className="h-10" data-testid="edit-existing-email" />
                      <Button type="button" onClick={loadExisting} disabled={loadingExisting} className="h-10" data-testid="edit-existing-load">{loadingExisting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Find'}</Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="text-center mt-6">
          <a href={event.signup_url} className="text-sm font-medium" style={{ color: theme }} data-testid="public-join-list-link">Join our email list for future gatherings →</a>
        </div>
      </div>
    </div>
  );
}
